import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  CreateAvailabilityScheduleDto,
  GetAvailabilityScheduleParams,
  staffAvailabilityServices,
  UpdateAvailabilityScheduleDto,
} from "./staff-availability.services";

export const STAFF_AVAILABILITY_KEYS = {
  all: ["staff-availability"] as const,
  lists: () => [...STAFF_AVAILABILITY_KEYS.all, "list"] as const,
  list: (filters: GetAvailabilityScheduleParams) => {
    const stableFilters = { ...filters };
    return [...STAFF_AVAILABILITY_KEYS.lists(), stableFilters] as const;
  },
  details: () => [...STAFF_AVAILABILITY_KEYS.all, "detail"] as const,
  detail: (id: string) => [...STAFF_AVAILABILITY_KEYS.details(), id] as const,
};

export const useGetStaffAvailabilities = (
  params?: GetAvailabilityScheduleParams,
  enabled: boolean = true
) => {
  return useQuery({
    queryKey: STAFF_AVAILABILITY_KEYS.list(params || {}),
    queryFn: () => staffAvailabilityServices.getAvailabilities(params),
    enabled: enabled,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
};

export const useGetStaffAvailabilityById = (id: string) => {
  return useQuery({
    queryKey: STAFF_AVAILABILITY_KEYS.detail(id),
    queryFn: () => staffAvailabilityServices.getAvailabilityById(id),
    enabled: !!id,
  });
};

export const useCreateStaffAvailability = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateAvailabilityScheduleDto) =>
      staffAvailabilityServices.createAvailability(data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: STAFF_AVAILABILITY_KEYS.all,
        refetchType: 'all'
      });
    },
  });
};

export const useUpdateStaffAvailability = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string;
      data: UpdateAvailabilityScheduleDto;
    }) => staffAvailabilityServices.updateAvailability(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: STAFF_AVAILABILITY_KEYS.all,
        refetchType: 'all'
      });
    },
  });
};

export const useDeleteStaffAvailability = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      staffAvailabilityServices.deleteAvailability(id),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: STAFF_AVAILABILITY_KEYS.all,
        refetchType: 'all'
      });
    },
  });
};

export const useCreateStaffAvailabilityList = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateAvailabilityScheduleDto[]) =>
      staffAvailabilityServices.createAvailabilityList(data),
    onSuccess: () => {
      console.log('🔄 Staff saved availability - invalidating ALL queries...');
      queryClient.invalidateQueries({
        queryKey: STAFF_AVAILABILITY_KEYS.all,
        refetchType: 'all'
      });
      queryClient.invalidateQueries({
        queryKey: ["staff-availability-details"],
        refetchType: 'all'
      });
      console.log('✅ All queries invalidated - admin will see staff changes in Team Calendar');
    },
  });
};

export const useUpdateStaffAvailabilityList = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: UpdateAvailabilityScheduleDto[]) =>
      staffAvailabilityServices.updateAvailabilityList(data),
    onSuccess: () => {
      console.log('🔄 Staff updated availability - invalidating ALL queries...');
      queryClient.invalidateQueries({
        queryKey: STAFF_AVAILABILITY_KEYS.all,
        refetchType: 'all'
      });
      queryClient.invalidateQueries({
        queryKey: ["staff-availability-details"],
        refetchType: 'all'
      });
      console.log('✅ All queries invalidated - admin will see staff changes in Team Calendar');
    },
  });
};
